import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ordersAPI, tablesAPI, menuAPI, staffAPI, customerAPI } from '../services/api';
import { formatCurrency, safeParseFloat } from '../utils/format';

import {
  ShoppingCart,
  Users,
  QrCode,
  TrendingUp,
  Clock,
  CheckCircle,
  DollarSign,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Eye,
  Plus,
  Activity,
  Table2,
  Target,
} from 'lucide-react';

const RestaurantDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch orders data
  const { data: ordersData, isLoading: ordersLoading } = useQuery(
    'orders',
    () => ordersAPI.getOrders(),
    {
      refetchInterval: 30000,
    }
  );

  const { data: dishesData } = useQuery(
    'dishes',
    () => menuAPI.getDishes()
  );

  // Fetch tables data
  const { data: tablesData, isLoading: tablesLoading } = useQuery(
    'tables',
    () => tablesAPI.getTables()
  );

  const { data: staffData } = useQuery(
    'staff',
    () => staffAPI.getStaff()
  );

  // ============= NEW: Fetch suggestion statistics =============
  const { data: suggestionStatsData, isLoading: suggestionStatsLoading } = useQuery(
    ['suggestion-stats', user?.restaurant_id || user?.id],
    () => customerAPI.getSuggestionStats(user?.restaurant_id || user?.id),
    {
      enabled: !!user?.restaurant_id || !!user?.id,
      refetchInterval: 60000, // Refresh every minute
      // Handle errors gracefully
      onError: (error) => {
        console.warn('Failed to fetch suggestion stats:', error);
      }
    }
  );
 
  const orders = ordersData?.data || [];
  const tables = tablesData?.data || [];
  const dishes = dishesData?.data || [];
  const staff = staffData?.data || [];

  // Process suggestion statistics
  const suggestionStats = suggestionStatsData?.data || {
    totalShown: 0,
    accepted: 0,
    ignored: 0,
    conversionRate: 0,
    revenue: 0,
    todayAccepted: 0
  };

  // Calculate statistics
  const pendingOrders = orders.filter(order => order.status === 'pending').length;
  const completedOrders = orders.filter(order => order.status === 'completed').length;
  const totalRevenue = orders
    .filter(order => order.payment_status === 'paid')
    .reduce((sum, order) => sum + safeParseFloat(order.total_amount), 0);

  const recentOrders = orders.slice(0, 5);

  // Check user role to determine permissions
  const userRole = user?.role || 'staff';
  const isManagerOrAdmin = ['admin', 'manager', 'owner'].includes(userRole);

  // Helper function to format badge text with counts
  const formatBadgeText = (key, count, fallback) => {
    const translation = t(key, fallback);
    if (typeof translation === 'string') {
      return translation.replace('{count}', count);
    }
    return fallback.replace('{count}', count);
  };

  const stats = [
    {
      icon: ShoppingCart,
      title: t('dashboard.stats.totalOrders', 'Total Orders'),
      value: orders.length,
      changeType: "positive",
      gradient: "from-blue-500 to-cyan-500",
      description: t('dashboard.stats.ordersToday', 'Orders today')
    },
    {
      icon: Clock,
      title: t('dashboard.stats.pendingOrders', 'Pending Orders'),
      value: pendingOrders,
      changeType: "positive",
      gradient: "from-yellow-500 to-amber-500",
      description: t('dashboard.stats.awaitingPreparation', 'Awaiting preparation')
    },
    {
      icon: CheckCircle,
      title: t('dashboard.stats.completed', 'Completed'),
      value: completedOrders,
      changeType: "positive",
      gradient: "from-green-500 to-emerald-500",
      description: t('dashboard.stats.readyForServing', 'Ready for serving')
    },
    {
      icon: DollarSign,
      title: t('dashboard.stats.revenue', 'Revenue'),
      value: formatCurrency(totalRevenue),
      changeType: "positive",
      gradient: "from-purple-500 to-violet-500",
      description: t('dashboard.stats.todaysEarnings', 'Today\'s earnings')
    },
    // ============= NEW: Suggestion Statistics Card =============
    {
      icon: Sparkles,
      title: t('dashboard.stats.suggestionsAccepted', 'Suggestions Accepted'),
      value: suggestionStats.accepted || 0,
      change: suggestionStats.todayAccepted > 0 ? `+${suggestionStats.todayAccepted} today` : "+0%",
      changeType: suggestionStats.todayAccepted > 0 ? "positive" : "neutral",
      gradient: "from-pink-500 to-rose-500",
      description: `${suggestionStats.conversionRate || 0}% ` + t('dashboard.stats.ConvRate', ' conversion rate')
    }
  ];

  // Quick actions - staff management only shown to managers/admins
  const quickActions = [
    {
      icon: TrendingUp,
      title: t('dashboard.quickActions.manageMenu', 'Manage Menu'),
      description: t('dashboard.quickActions.manageMenuDesc', 'Add dishes & categories'),
      gradient: "from-blue-500 to-cyan-500",
      href: "/menu",
      badge: formatBadgeText('dashboard.quickActions.itemsCount', dishes.length, `${dishes.length} items`)
    },
    {
      icon: QrCode,
      title: t('dashboard.quickActions.manageTables', 'Manage Tables'),
      description: t('dashboard.quickActions.manageTablesDesc', 'Generate QR codes'),
      gradient: "from-green-500 to-emerald-500",
      href: "/tables",
      badge: formatBadgeText('dashboard.quickActions.tablesCount', tables.length, `${tables.length} tables`)
    },
    {
      icon: ShoppingCart,
      title: t('dashboard.quickActions.viewOrders', 'View Orders'),
      description: t('dashboard.quickActions.viewOrdersDesc', 'Track & manage orders'),
      gradient: "from-yellow-500 to-amber-500",
      href: "/orders",
      badge: formatBadgeText('dashboard.quickActions.activeOrders', orders.length, `${orders.length} active`)
    },
    // Only show staff management for managers/admins
    ...(isManagerOrAdmin ? [{
      icon: Users,
      title: t('dashboard.quickActions.staffManagement', 'Staff Management'),
      description: t('dashboard.quickActions.staffManagementDesc', 'Manage team members'),
      gradient: "from-purple-500 to-violet-500",
      href: "/staff",
      badge: formatBadgeText('dashboard.quickActions.staffCount', staff.length, `${staff.length} staff`)
    }] : [])
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'in_progress': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'completed': return 'text-green-600 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <Activity className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': t('orders.status.pending', 'pending'),
      'in_progress': t('orders.status.inProgress', 'in progress'),
      'completed': t('orders.status.completed', 'completed'),
      'cancelled': t('orders.status.cancelled', 'cancelled')
    };
    return statusMap[status] || status;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 overflow-x-hidden p-7">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className={`mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="w-full">
              <div className="flex items-center gap-3 mb-2">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  <Sparkles className="h-4 w-4" />
                  {t('dashboard.header.badge', 'Restaurant Dashboard')}
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 break-words">
                {t('dashboard.header.welcome', 'Welcome back, {name}!').replace('{name}', user?.name || user?.restaurant_name || '')}
              </h1>
              <p className="text-gray-600 mt-2 text-sm md:text-base max-w-4xl">
                {t('dashboard.header.subtitle', 'Here\'s what\'s happening at your restaurant today. Monitor orders, track revenue, and manage operations in real-time.')}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions - Moved to top for immediate access */}
        <div className={`mb-8 transition-all duration-700 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 w-full">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">
              {t('dashboard.quickActions.title', 'Quick Actions')}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={index}
                    to={action.href}
                    className="group p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gray-200 w-full"
                  >
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className={`p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r ${action.gradient} text-white group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                        <Icon className="h-4 w-4 md:h-5 md:w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{action.title}</h3>
                          <span className="text-xs bg-white px-2 py-1 rounded-full text-gray-600 font-medium flex-shrink-0 ml-2">
                            {action.badge}
                          </span>
                        </div>
                        <p className="text-xs md:text-sm text-gray-600 truncate">{action.description}</p>
                      </div>
                      <ArrowUp className="h-3 w-3 md:h-4 md:w-4 text-gray-400 transform -rotate-45 group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Orders - Moved to second position for immediate visibility of active orders */}
        <div className={`mb-8 transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 w-full">
            <div className="p-4 md:p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg md:text-xl font-bold text-gray-900">
                  {t('dashboard.recentOrders.title', 'Recent Orders')}
                </h2>
                <Link
                  to="/orders"
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
                >
                  {t('common.viewAll', 'View all')}
                  <ArrowUp className="h-3 w-3 md:h-4 md:w-4 transform -rotate-45" />
                </Link>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {ordersLoading ? (
                <div className="flex justify-center py-8 md:py-12">
                  <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : recentOrders.length === 0 ? (
                <div className="text-center py-8 md:py-12">
                  <ShoppingCart className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-2 text-sm md:text-base">
                    {t('dashboard.recentOrders.noOrders', 'No orders yet')}
                  </p>
                  <p className="text-xs md:text-sm text-gray-400">
                    {t('dashboard.recentOrders.noOrdersDesc', 'Orders will appear here as customers place them')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl hover:bg-gray-100 transition-all duration-300 group border border-transparent hover:border-gray-200 w-full"
                    >
                      <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                        <div className={`p-2 md:p-3 rounded-lg md:rounded-xl border ${getStatusColor(order.status)} flex-shrink-0`}>
                          {getStatusIcon(order.status)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900 text-sm md:text-base truncate">
                              {t('dashboard.recentOrders.orderNumber', 'Order #{number}').replace('{number}', order.id)}
                            </p>
                            {order.payment_status === 'paid' && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex-shrink-0">
                                {t('orders.payment.paid', 'Paid')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 truncate">
                            {t('dashboard.recentOrders.tableInfo', 'Table {table} • {amount}')
                              .replace('{table}', order.table_number)
                              .replace('{amount}', formatCurrency(order.total_amount))
                            }
                          </p>
                          <div className="flex items-center gap-2 md:gap-4 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500">
                              {new Date(order.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)} hidden sm:inline-block`}>
                          {getStatusText(order.status)}
                        </span>

                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Link
                            to="/orders"
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
                          >
                            <button className="p-1 md:p-2 hover:bg-white rounded-lg transition-colors duration-300">
                              <Eye className="h-3 w-3 md:h-4 md:w-4 text-gray-400" />
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 mb-8 transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="group bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border border-gray-100 w-full"
              >
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className={`p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r ${stat.gradient} text-white group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                  <div className={`flex items-center gap-1 text-xs md:text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' : stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
                    }`}>
                    {stat.change && (
                      <>
                        {stat.changeType === 'positive' ? <ArrowUp className="h-3 w-3" /> :
                          stat.changeType === 'negative' ? <ArrowDown className="h-3 w-3" /> : null}
                        <span>{stat.change}</span>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 truncate">
                    {stat.title === 'Suggestions Accepted' && suggestionStatsLoading ? '...' : stat.value}
                  </p>
                  <p className="text-sm font-medium text-gray-600 mb-1 md:mb-2 truncate">{stat.title}</p>
                  <p className="text-xs text-gray-500 truncate">{stat.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* ============= NEW: Suggestion Analytics Section ============= */}
        <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8 transition-all duration-700 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {t('dashboard.suggestionAnalytics.title', 'Suggestion Performance')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('dashboard.suggestionAnalytics.subtitle', 'Track how well suggestions convert to sales')}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  {t('dashboard.suggestionAnalytics.totalShown', 'Total Shown')}
                </span>
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Eye className="h-4 w-4 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{suggestionStats.totalShown || 0}</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  {t('dashboard.suggestionAnalytics.accepted', 'Accepted')}
                </span>
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{suggestionStats.accepted || 0}</p>
              {suggestionStats.todayAccepted > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  +{suggestionStats.todayAccepted} {t('dashboard.suggestionAnalytics.today', 'today')}
                </p>
              )}
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  {t('dashboard.suggestionAnalytics.conversionRate', 'Conversion Rate')}
                </span>
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-violet-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{suggestionStats.conversionRate || 0}%</p>
              <p className="text-sm text-gray-500 mt-1">
                {t('dashboard.suggestionAnalytics.ofShown', 'of suggestions shown')}
              </p>
            </div>
          </div>

          <div className="mt-6 bg-gray-50 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {t('dashboard.suggestionAnalytics.revenueGenerated', 'Revenue Generated from Suggestions')}
                </p>
                <p className="text-lg font-bold text-gray-900 mt-1">
                  CHF {suggestionStats.revenue ? suggestionStats.revenue.toFixed(2) : '0.00'}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </div>
        </div>
        {/* ============= END NEW SECTION ============= */}

        {/* Tables Overview - Moved to bottom */}
        <div className={`bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 transition-all duration-700 delay-500 w-full ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900">
              {t('dashboard.tables.title', 'Tables Overview')}
            </h2>
            <Link
              to="/tables"
              className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
            >
              {t('dashboard.tables.manage', 'Manage')}
              <ArrowUp className="h-3 w-3 md:h-4 md:w-4 transform -rotate-45" />
            </Link>
          </div>

          {tablesLoading ? (
            <div className="flex justify-center py-6 md:py-8">
              <div className="w-5 h-5 md:w-6 md:h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center py-4 md:py-6">
              <Table2 className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 text-gray-300" />
              <p className="text-gray-500 mb-2 text-sm md:text-base">
                {t('dashboard.tables.noTables', 'No tables created yet')}
              </p>
              <p className="text-xs md:text-sm text-gray-400 mb-3 md:mb-4">
                {t('dashboard.tables.noTablesDesc', 'Create tables to generate QR codes for customers')}
              </p>
              <Link
                to="/tables"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-2 md:px-4 md:py-2 rounded-lg md:rounded-xl font-medium hover:bg-blue-700 transition-colors duration-300 text-sm"
              >
                <Plus className="h-3 w-3 md:h-4 md:w-4" />
                {t('dashboard.tables.createFirstTable', 'Create First Table')}
              </Link>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {tables.slice(0, 6).map((table) => (
                  <div
                    key={table.id}
                    className="group p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl text-center hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gray-200"
                  >
                    <div className="relative">
                      <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg md:rounded-xl flex items-center justify-center text-white mx-auto mb-2 md:mb-3 group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300`}>
                        <Table2 className="h-4 w-4 md:h-6 md:w-6" />
                      </div>
                      {table.status === 'occupied' && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    <p className="font-semibold text-gray-900 text-sm md:text-base">
                      {t('dashboard.tables.tableNumber', 'Table {number}').replace('{number}', table.table_number)}
                    </p>
                    <p className={`text-xs font-medium ${table.status === 'occupied' ? 'text-red-600' : 'text-green-600'
                      }`}>
                      {table.status === 'occupied'
                        ? t('tables.status.occupied', 'Occupied')
                        : t('tables.status.available', 'Available')
                      }
                    </p>
                  </div>
                ))}
              </div>

              {tables.length > 6 && (
                <div className="text-center pt-3 md:pt-4 border-t border-gray-100">
                  <p className="text-xs md:text-sm text-gray-600">
                    {t('dashboard.tables.moreTables', '+{count} more tables').replace('{count}', tables.length - 6)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantDashboard;